import { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from "react";

import { Spinner } from "@shapr3d/frontend-lib";
import Player from "@vimeo/player";
import classNames from "classnames";
import { createPortal } from "react-dom";

import { useEventListener } from "../../hooks/useEventListener";

import style from "./VimeoVideo.module.css";

export interface OverlayPlayerProps {
  open: boolean;
  onClose: () => void;
  children: React.ReactNode;
  videoId: string;
  onReady?: (player: Player) => void;
}
export type OverlayPlayerHandle = {
  loadAndPlay: (id: string) => Promise<void>;
};

export const Thumbnail: React.FC<
  React.ImgHTMLAttributes<HTMLImageElement> & { isLoading: boolean }
> = (props) => {
  return (
    <div
      className={classNames(props.className, style.imageContainer)}
      onClick={(e: React.MouseEvent<HTMLImageElement>) => {
        props.onClick?.(e);
      }}
    >
      <img style={{ width: "100%" }} src={props.src} alt={props.alt} />
      {!props.isLoading && (
        <>
          <div role="button" className={style.playButtonBackground} />
          <div role="button" className={style.playButton} />
        </>
      )}
    </div>
  );
};

export const OverlayPlayer = forwardRef<OverlayPlayerHandle, OverlayPlayerProps>(
  ({ open, onClose, children, videoId, onReady }, ref) => {
    const playerRef = useRef<Player | null>(null);
    const playerContainerRef = useRef<HTMLDivElement | null>(null);

    useEffect(() => {
      (async () => {
        if (!playerContainerRef.current) return;
        if (playerRef.current) return;
        const p = new Player(playerContainerRef.current, {
          id: videoId,
          autoplay: false,
          responsive: true,
          title: false,
          byline: false,
          portrait: false,
        });

        playerRef.current = p;
        await p.ready();
        onReady?.(p);
      })();
    }, [onReady, videoId]);

    useEffect(() => {
      return () => {
        if (playerRef.current) {
          playerRef.current.unload?.();
          playerRef.current.destroy?.();
          playerRef.current = null;
        }
      };
    }, []);

    const [isLoaded, setIsLoaded] = useState(false);

    const onKeyDown = useCallback(
      (e: KeyboardEvent) => {
        if (e.key === "Escape" && open) onClose();
      },
      [onClose, open]
    );
    useEventListener("keydown", onKeyDown);

    useEffect(() => {
      const p = playerRef.current;
      if (!p) return;
      if (!open) {
        setIsLoaded(false);
        (async () => {
          await p.pause().catch(() => {});
          await p.setCurrentTime(0).catch(() => {});
        })();
        return;
      }
    }, [open]);

    useImperativeHandle(
      ref,
      () => ({
        loadAndPlay: async (id: string) => {
          const p = playerRef.current;
          if (!p) return;

          await p.ready();

          const currentId = await p.getVideoId();
          if (String(currentId) !== String(id)) {
            await p.loadVideo(id);
          }
          setIsLoaded(true);

          try {
            await p.play();
          } catch {
            await p.setMuted(true).catch(() => {});
            await p.play().catch(() => {});
          }
        },
      }),
      []
    );

    return createPortal(
      <div className={classNames(style.wrapper, open && style.open)}>
        <div
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) onClose();
          }}
          className={classNames(style.backdrop, open && style.open)}
        >
          <div
            className={style.playerWithCaptionContainer}
            onMouseDown={(e) => e.stopPropagation()}
          >
            <div className={classNames(style.playerWrapper, open && style.open)}>
              <div ref={playerContainerRef} className={style.playerContainer} />
              {!isLoaded && open && (
                <div className={style.loadingContainer}>
                  <Spinner />
                </div>
              )}
            </div>

            {open && <div className={style.captionContainer}>{children}</div>}
          </div>
        </div>
      </div>,
      document.getElementById("modal-root") ?? document.body
    );
  }
);
